/**
 * Containers: the only place a screen meets the repository.
 *
 * Each one loads, handles the two failure states, and hands plain data down.
 * The screens below them stay pure — which is what makes them testable and
 * what made the fixture-to-database swap a change in this file alone.
 */

import { useCallback, useState } from 'react';
import { useAsync, useRepo } from '../data/RepoProvider.js';
import { Failed, Loading } from '../components/Loading.js';
import { Dashboard } from './Dashboard.js';
import { Catalog, Customers, Jobs } from './Lists.js';
import { NewJob, type NewJobDraft } from './NewJob.js';
import { Rates } from './Admin.js';
import type { RateProfile } from '../calc/types.js';

export function DashboardView() {
  const repo = useRepo();
  const r = useAsync(() => repo.dashboardMetrics(), [repo]);
  if (r.state === 'loading') return <Loading what="the dashboard" />;
  if (r.state === 'error') return <Failed what="the dashboard" message={r.message} />;
  return <Dashboard metrics={r.value} />;
}

export function JobsView({ onOpen }: { onOpen: (reference: string) => void }) {
  const repo = useRepo();
  const [creating, setCreating] = useState(false);
  // Bumped after an inline edit, the same refetch-after-write pattern as
  // Customers and Catalog — what's on screen is what was actually saved.
  const [revision, setRevision] = useState(0);
  const r = useAsync(() => repo.listJobs(), [repo, revision]);

  if (creating) {
    return <NewJobView onDone={() => setCreating(false)} onOpen={onOpen} />;
  }
  if (r.state === 'loading') return <Loading what="jobs" />;
  if (r.state === 'error') return <Failed what="jobs" message={r.message} />;
  return <Jobs rows={r.value} onOpen={onOpen} onNew={() => setCreating(true)}
    onEdit={async (reference, patch) => {
      await repo.updateJobDetails(reference, patch);
      setRevision((n) => n + 1);
    }} />;
}

function NewJobView({ onDone, onOpen }: {
  onDone: () => void;
  onOpen: (reference: string) => void;
}) {
  const repo = useRepo();
  const [error, setError] = useState<string | null>(null);
  const r = useAsync(() => repo.listCustomers(), [repo]);
  if (r.state === 'loading') return <Loading what="customers" />;
  // The customer list failing must not block starting a job — a shop with a car
  // on the ramp needs to begin, and a new customer needs no list at all.
  const customers = r.state === 'ready' ? r.value : [];

  const create = async (d: NewJobDraft) => {
    setError(null);
    try {
      const { reference } = await repo.createJob({
        customerId: d.customerId,
        customerName: d.newCustomerName,
        customerEmail: d.newCustomerEmail.trim(),
        customerNeedsApproval: d.newCustomerNeedsApproval,
        vehicle: {
          vin: d.vin.trim() || undefined,
          plate: d.plate.trim() || undefined,
          year: d.year ? Number(d.year) : undefined,
          make: d.make.trim(),
          model: d.model.trim(),
          trim: d.trim.trim() || undefined,
          odometer: d.odometer ? Number(d.odometer) : undefined,
          decodeSource: d.decodeSource,
        },
        impact: d.impact ?? 'front',
      });
      // Straight into the job. The estimator is standing at the car; making
      // them find it in a list they just added to is a step for nobody.
      onOpen(reference);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      {error && <Failed what="the new job" message={error} />}
      <NewJob customers={customers} onCancel={onDone}
              onCreate={(d: NewJobDraft) => void create(d)} />
    </>
  );
}

export function CustomersView() {
  const repo = useRepo();
  // Bumping this refetches after a write, so what is on screen is what was
  // saved — not an optimistic guess that quietly diverges from the database.
  const [revision, setRevision] = useState(0);
  const r = useAsync(() => repo.listCustomers(), [repo, revision]);

  const toggle = useCallback(async (id: string, needsApproval: boolean) => {
    await repo.setCustomerApproval(id, needsApproval);
    setRevision((n) => n + 1);
  }, [repo]);

  if (r.state === 'loading') return <Loading what="customers" />;
  if (r.state === 'error') return <Failed what="customers" message={r.message} />;
  return <Customers rows={r.value} onToggleApproval={(id, v) => void toggle(id, v)}
    onCreate={async (input) => {
      await repo.createCustomer(input);
      setRevision((n) => n + 1);
    }}
    onUpdate={async (id, patch) => {
      await repo.updateCustomer(id, patch);
      setRevision((n) => n + 1);
    }} />;
}

export function CatalogView({ focus, onFocusHandled }: {
  /** Part name to search for and open, when arriving from a priced line. */
  focus?: string | null;
  onFocusHandled?: () => void;
} = {}) {
  const repo = useRepo();
  // Bumped after an import so the list refetches — the same revision pattern
  // the job shell uses, rather than a second way of doing the same thing.
  const [revision, setRevision] = useState(0);
  const r = useAsync(() => repo.listCatalog(), [repo, revision]);
  // Rates come along so the form can show what the hours are worth as they are
  // typed — the fastest way to answer "is this price per hour or total?".
  const rates = useAsync(() => repo.getRateProfile(), [repo]);
  if (r.state === 'loading') return <Loading what="the catalog" />;
  if (r.state === 'error') return <Failed what="the catalog" message={r.message} />;
  return <Catalog rows={r.value} focus={focus} onFocusHandled={onFocusHandled}
    profile={rates.state === 'ready' ? rates.value : undefined}
    onAdd={async (entry) => {
      await repo.addCatalogEntry(entry);
      setRevision((n) => n + 1);
    }}
    onRemoveRow={async (key) => {
      await repo.removeCatalogEntry(key);
      setRevision((n) => n + 1);
    }}
    onImport={async (entries) => {
    // Sequential on purpose: a shop file is hundreds of rows, and firing
    // hundreds of parallel writes at a small VPS is how an import half-lands.
    for (const e of entries) await repo.addCatalogEntry(e);
    setRevision((n) => n + 1);
  }} onEdit={async (entry) => {
    await repo.addCatalogEntry(entry);
    setRevision((n) => n + 1);
  }} />;
}

/**
 * Rates, connected to the shop's own profile.
 *
 * It used to render `FIXTURE_PROFILE` with a local `setState` — so a shop
 * looked at somebody else's door rate, changed it, and lost the change on
 * refresh while every estimate carried on pricing at the seeded figure.
 *
 * Edits are held locally and written on Save rather than on every keystroke:
 * a rate is a deliberate decision, and half-typed "8" on the way to "85"
 * should never be the shop's rate even for a second.
 */
export function RatesView() {
  const repo = useRepo();
  const [revision, setRevision] = useState(0);
  const r = useAsync(() => repo.getRateProfile(), [repo, revision]);
  const [edited, setEdited] = useState<RateProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (r.state === 'loading') return <Loading what="your rates" />;
  if (r.state === 'error') return <Failed what="your rates" message={r.message} />;

  const profile = edited ?? r.value;
  const dirty = edited !== null;

  return (
    <>
      <Rates profile={profile} onChange={setEdited} />
      <div className="ratesave">
        {error && <span className="err">{error}</span>}
        <button className="btn primary" disabled={!dirty || saving}
                onClick={async () => {
                  setSaving(true);
                  setError(null);
                  try {
                    await repo.saveRateProfile(profile);
                    setEdited(null);
                    setRevision((n) => n + 1);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : String(err));
                  } finally {
                    setSaving(false);
                  }
                }}>
          {saving ? 'Saving…' : dirty ? 'Save rates' : 'Saved'}
        </button>
        {dirty && (
          <button className="btn" onClick={() => { setEdited(null); setError(null); }}>
            Discard changes
          </button>
        )}
        {dirty && (
          <span className="muted small">
            Not saved yet — every job prices from the saved figures.
          </span>
        )}
      </div>
    </>
  );
}
